/**
 * 子应用 SSO 跳转 Hook
 *
 * 封装 navigateToSubApp 的 loading 状态管理，
 * 调用方只需关心"跳到哪个子应用"，不需要处理 ssoMode / async / try-catch 等细节。
 *
 * @example
 * const { navigating, navigate } = useSubAppNavigation();
 *
 * const handleClick = async () => {
 *   if (!getToken()) { onRequestLogin?.('toolbox'); return; }
 *   await navigate('toolbox');
 * };
 *
 * <Button disabled={navigating}>立即体验</Button>
 */

import { useState, useCallback } from "react";
import { navigateToSubApp, isSSOAsync } from "@/lib/utils/sso";

export function useSubAppNavigation() {
  const [navigating, setNavigating] = useState(false);

  const navigate = useCallback(async (appKey: string): Promise<void> => {
    const asyncMode = isSSOAsync(appKey);
    if (asyncMode) setNavigating(true);
    let navigated = false;
    try {
      await navigateToSubApp(appKey);
      navigated = true; // 正常 resolve 说明已发起跳转（含降级），页面即将卸载
    } catch {
      // navigateToSubApp 内部已有 API 降级逻辑；
      // 此处仅兜底 NOT_AUTHENTICATED 等前置校验异常
    } finally {
      // 仅在未发起跳转（即抛出异常）时才重置 loading，
      // 避免页面跳转前 setNavigating(false) 导致按钮短暂闪烁
      if (asyncMode && !navigated) setNavigating(false);
    }
  }, []);

  return { navigating, navigate };
}
